/**
 * Casbin policies — sv_permissions, through the gateway's `/permissions/*` family.
 *
 * ## Two different things called "domain"
 *
 * `x-company-id` decides the domain this CALL is authorized in. `domain` in the body
 * (or the query) names the domain the RULE is written in. They are not the same field
 * and the gateway does not derive one from the other — but it does compare them:
 * `domain_must_match_scope` (`handlers/perms.rs`) lets a caller who sent
 * `x-company-id` name `company:{that id}` and nothing else — not `system`, not `*`,
 * not another company, not even a department inside their own. Anything else is
 * `403 domain_scope_mismatch`.
 *
 * Every function here therefore BUILDS the domain from the same `companyId` it sends
 * as the header. There is no parameter for it, because there is no legal second value
 * a cabinet caller could pass.
 *
 * ## What can and cannot be read back
 *
 * `GET /permissions/user/{id}` reads one account's DIRECT rows — never what a role
 * grants them. `GET /permissions/me` reads the caller's own EFFECTIVE set. There is no
 * route anywhere that lists a ROLE's policies, so {@link grantRolePermission} and its
 * twin are a write-only surface: both are idempotent, both answer `{"success": true}`
 * whether or not anything changed, and neither 404s nor 409s. A UI over them must
 * present an action, never a checkbox claiming to show current state.
 */
import {request, requestInCompany} from '@/shared/api';
import {companyDomain} from './domain';
import {
  EFFECT_ALLOW,
  type EffectivePermission,
  type EffectivePermissions,
  type PolicyEffect,
  type PolicyRule,
  type UserPolicies,
} from './types';

/**
 * The caller's own effective permissions in ONE domain.
 *
 * Deliberately a bare `request()`: this route is waived from the Casbin check entirely
 * (`should_skip_permission`) and reads no `x-company-id` at all — the `domain` query
 * parameter is the only scope it has. Sending the header would not be an error, but it
 * would suggest to the next reader that it narrows something. It does not.
 */
export async function fetchMyPermissions(
  domain: string,
): Promise<EffectivePermission[]> {
  const res = await request<EffectivePermissions>(
    `/permissions/me?domain=${encodeURIComponent(domain)}`,
  );
  return res.permissions ?? [];
}

/**
 * One member's DIRECT policy rows in this company.
 *
 * Gated on `members, read` — not on `permissions` — so anyone who can open the member
 * list can also see what has been granted to a member by hand. What it does NOT show
 * is what their ROLE gives them: those rows are written against the role code as the
 * subject, and this route reads only rows whose subject is the account.
 */
export async function fetchUserPolicies(
  companyId: string,
  userId: string,
): Promise<PolicyRule[]> {
  const domain = companyDomain(companyId);
  const res = await requestInCompany<UserPolicies>(
    `/permissions/user/${encodeURIComponent(userId)}?domain=${encodeURIComponent(domain)}`,
    companyId,
  );
  return res.policies ?? [];
}

export type UserPermissionInput = {
  /** The account id — `Member.account_id`, never the membership id. */
  userId: string;
  object: string;
  action: string;
  /** Defaults to allow; a deny row is how one action is carved out of a `manage`. */
  effect?: PolicyEffect;
};

function userBody(companyId: string, input: UserPermissionInput) {
  return JSON.stringify({
    user_id: input.userId,
    domain: companyDomain(companyId),
    object: input.object,
    action: input.action,
    effect: input.effect ?? EFFECT_ALLOW,
  });
}

/** Grant one permission straight to an account, bypassing roles entirely. */
export function grantUserPermission(
  companyId: string,
  input: UserPermissionInput,
): Promise<unknown> {
  return requestInCompany<unknown>('/permissions/user/assign', companyId, {
    method: 'POST',
    body: userBody(companyId, input),
  });
}

/**
 * Remove one direct rule from an account.
 *
 * `effect` is part of the key, not a hint: revoking `(devices, read)` under allow does
 * not remove the same triple stored under deny. Pass the effect the row actually has —
 * {@link effectOf} on the row you read is the way to get it right.
 */
export function revokeUserPermission(
  companyId: string,
  input: UserPermissionInput,
): Promise<unknown> {
  return requestInCompany<unknown>('/permissions/user/revoke', companyId, {
    method: 'POST',
    body: userBody(companyId, input),
  });
}

export type RolePermissionInput = {
  /** The Casbin subject here is a ROLE CODE, not an account id. */
  role: string;
  object: string;
  action: string;
  effect?: PolicyEffect;
};

function roleBody(companyId: string, input: RolePermissionInput) {
  return JSON.stringify({
    role: input.role,
    domain: companyDomain(companyId),
    object: input.object,
    action: input.action,
    effect: input.effect ?? EFFECT_ALLOW,
  });
}

/**
 * Change what EVERYONE holding a role can do in this company.
 *
 * Write-only (see the module note) and wide: this is not "give Ivan the reports", it is
 * "every accountant in this company, now and in future, gets the reports".
 */
export function grantRolePermission(
  companyId: string,
  input: RolePermissionInput,
): Promise<unknown> {
  return requestInCompany<unknown>('/permissions/role/assign', companyId, {
    method: 'POST',
    body: roleBody(companyId, input),
  });
}

/** Remove a permission from a role. Idempotent — see {@link grantRolePermission}. */
export function revokeRolePermission(
  companyId: string,
  input: RolePermissionInput,
): Promise<unknown> {
  return requestInCompany<unknown>('/permissions/role/revoke', companyId, {
    method: 'POST',
    body: roleBody(companyId, input),
  });
}

/**
 * Flip an existing rule between allow and deny.
 *
 * `currentEffect` must be what the rule has NOW. Get it wrong and the route does not
 * complain: the remove half matches nothing, the add half still runs, and the triple
 * ends up present under both effects — the opposite of the clean swap the name
 * promises. Always derive it from a row just read, with `effectOf`.
 */
export function togglePermission(
  companyId: string,
  input: {
    /** Role code or account id — whichever the rule was written against. */
    subject: string;
    object: string;
    action: string;
    currentEffect: PolicyEffect;
  },
): Promise<unknown> {
  return requestInCompany<unknown>('/permissions/toggle', companyId, {
    method: 'POST',
    body: JSON.stringify({
      subject: input.subject,
      domain: companyDomain(companyId),
      object: input.object,
      action: input.action,
      current_effect: input.currentEffect,
    }),
  });
}
